import { getCorrelationContext } from './correlation-context';
import { IS_NATIVE_APP_CLIENT, poll, heapTrack, recordCustomEventToNewRelic } from './helpers';
import { Customer } from './environment';
import { getPageType } from './gtm-data-layer';

const FULLSTORY_SESSION_STARTED_EVENT_NAME = 'fullstory_session_started';

let lastTrackedFullStorySessionId: string | null = null;
let hasInitializedFullStorySessionTracking = false;

export const identifyUserInFullStory = () => {
  try {
    const userId = Customer?.id?.toString();
    const userEmail = Customer?.email;

    if (userId) {
      executeFS('setIdentity', {
        uid: userId,
        properties: {
          displayName: userEmail,
          email: userEmail,
        },
      });
    } else {
      executeFS('setIdentity', { anonymous: true });
    }
  } catch (error) {
    console.error('Error capturing user on page load:', error);
  }
};

export const getFullStorySessionId = () => {
  const fs = window.FS;

  if (typeof fs !== 'function') {
    return null;
  }

  try {
    const sessionId = fs('getSession', { format: 'id' });
    return sessionId?.toString() || null;
  } catch {
    return null;
  }
};

export const getSierraSafeFullStorySessionId = () => {
  const sessionId = getFullStorySessionId();
  if (!sessionId) return null;
  return encodeURIComponent(sessionId);
};

const getFullStorySessionStartedPayload = (sessionId: string) =>
  Object.fromEntries(
    Object.entries({
      ...getCorrelationContext(),
      fullStorySessionId: sessionId,
    }).filter(([, value]) => value !== null && value !== undefined)
  );

const trackFullStorySessionStarted = (sessionId: string) => {
  if (!sessionId || sessionId === lastTrackedFullStorySessionId) return;
  lastTrackedFullStorySessionId = sessionId;

  try {
    const payload = getFullStorySessionStartedPayload(sessionId);

    recordCustomEventToNewRelic(FULLSTORY_SESSION_STARTED_EVENT_NAME, payload);
    heapTrack(FULLSTORY_SESSION_STARTED_EVENT_NAME, payload);
  } catch (error) {
    console.error('Failed to track FullStory session start', error);
  }
};

const attachFullStorySessionObserver = () => {
  try {
    window.FS?.('observe', {
      type: 'start',
      callback: () => {
        const sessionId = getFullStorySessionId();
        if (sessionId) {
          trackFullStorySessionStarted(sessionId);
        }
      },
    });
  } catch {
    return;
  }
};

const startFullStorySessionTracking = () => {
  const sessionId = getFullStorySessionId();
  if (sessionId) {
    trackFullStorySessionStarted(sessionId);
  }

  attachFullStorySessionObserver();
};

export const initializeFullStorySessionTracking = () => {
  if (hasInitializedFullStorySessionTracking || IS_NATIVE_APP_CLIENT) return;
  hasInitializedFullStorySessionTracking = true;

  if (typeof window.FS === 'function') {
    startFullStorySessionTracking();
    return;
  }

  const existingFsReadyHandler = window._fs_ready;
  window._fs_ready = () => {
    try {
      existingFsReadyHandler?.();
    } finally {
      startFullStorySessionTracking();
    }
  };
};

export const fireFSMenuLinkTracking = (link: HTMLAnchorElement) => {
  try {
    const { dataset, innerText, href } = link;

    const properties = {
      link_id: dataset.nav,
      link_text: innerText,
      link_url: href,
    };

    executeFS('trackEvent', {
      name: 'fs_menu_link',
      properties,
    });
  } catch (err) {
    console.error(err);
  }
};

export const pushPageViewEventToFS = (additionalProps: object = {}) => {
  try {
    if (IS_NATIVE_APP_CLIENT) return;

    const { host, pathname } = window.location;

    executeFS('setProperties', {
      type: 'page',
      properties: {
        pageName: `${host}${pathname}`,
        page_type: getPageType(pathname),
        user_id: Customer?.id?.toString() || '',
        ...additionalProps,
      },
    });
  } catch (err) {
    console.error('Error caputring FS page properties', err);
  }
};

export const executeFS = (method: string, options: Record<string, unknown> = {}) => {
  poll(() => typeof window.FS === 'function', 5000, 1000)
    .then(() => window?.FS?.(method, options))
    .catch((error) => {
      console.log(error);
    });
};
